import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cpu, Save } from "lucide-react";
import type { Agent } from "@/types";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { useUpdateModelPolicy, type ModelPolicyPatch } from "@/api/mutations/modelPolicy";
import { usePatchManaged } from "@/api/mutations/agentConfig";
import { useAgentModels } from "@/api/queries/models";
import { mitAltwert, modelOptionLabel } from "@/lib/modelTarget";

// #224R: Modell-Governance-Sektion auf der Agent-Detail-Seite. Zwei klar getrennte
// Hebel + EINE dynamische Modell-Liste (GET /models, von den Brains via
// supportedModels() gemeldet):
//   1. Admin-Fix  → config.managed.model (Config-API). Gesetzt ⇒ der Master pusht
//      kein options.model mehr, das Brain liest den Fix aus den settings, der Owner
//      kann NICHT mehr per /model wechseln.
//   2. Whitelist  → modelPolicy.allowedModels (model-policy-API). Schränkt ein, aus
//      welchen Modellen der Owner per /model wählen darf (leer ⇒ ganzer Katalog).
// Ein Speichern-Button patcht die jeweils geänderten Hebel.
//
// #P001 (UC-1): Wählbar ist nicht mehr nur der Katalog — jeder AKTIVE Provider-Slot ist ein
// eigener Modellname.
//
// #P001 (Live-Fund 3, Arben 2026-09-03): die Karte hatte deswegen ZWEI Modell-Quellen — die
// Whitelist-Checkboxen und der Admin-Fix aus dem Plattform-Katalog (`GET /models`, nur die
// Anthropic-Aliase), die Kontext-Defaults zwei Zeilen tiefer aus dem Universum DIESES Agenten
// (`GET /agents/:id/models` → `universe`). Ein Slot liess sich deshalb nur als FREITEXT in die
// Whitelist eintragen — ein Name, den dieselbe Karte weiter unten längst mit Ziel-Label anbietet.
// Das verstösst gegen „Auswahl statt Tippen" (admin-gepflegte Objekte nie als Freitext).
// Seither gilt: **das Universum ist die EINE Quelle** für Whitelist UND Admin-Fix; der Katalog
// wird hier gar nicht mehr gelesen. Der Freitext bleibt als reiner Version-Skew-Rückfall, wenn
// die Auskunft fehlt (älterer Master) oder nicht abrufbar ist.
//
// #P001 (UC-3): dritter Hebel — die KONTEXT-DEFAULTS (`modelPolicy.defaults.chat/case`). Sie
// sagen, womit eine neue Unterhaltung bzw. ein neuer Prozess-Lauf STARTET; gewählt wird aus dem
// Universum des Agenten (GET /agents/:id/models → `universe`), nicht aus der Owner-Whitelist —
// der Admin darf einen Default ausserhalb davon setzen.

// Haus-Select-Stil (wie ProviderRoutingSection / Instanz-Picker) — kein Radix-Select, damit
// Tests mit fireEvent.change arbeiten können.
const SELECT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-base px-3 py-2 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-base disabled:cursor-not-allowed disabled:opacity-50";

type Kontext = "chat" | "case";

export function ModelPolicySection({ agent }: { agent: Agent }) {
  const patchManaged = usePatchManaged();
  const updatePolicy = useUpdateModelPolicy(agent.id);
  // #P001 (UC-3 + Live-Fund 3): das Universum DIESES Agenten (aktive Slots + Aliase/Katalog) —
  // die EINZIGE Modell-Quelle dieser Karte. Derselbe Endpoint wie der Instanz-Picker (UC-2).
  const agentModels = useAgentModels(agent.id);

  // Lokaler Entwurf (dirty-Tracking) — aus dem Agent hydrieren, bei Refetch nur
  // übernehmen solange der User den jeweiligen Hebel nicht verändert hat.
  // Version-Skew-Härtung (wie DiscoverySection): ein alter Master ohne modelPolicy
  // im AgentResponse darf die Sektion nicht crashen.
  const [fixedModel, setFixedModel] = useState<string | null>(agent.modelPolicy?.fixedModel ?? null);
  const [allowedModels, setAllowedModels] = useState<string[]>(agent.modelPolicy?.allowedModels ?? []);
  const [fixDirty, setFixDirty] = useState(false);
  const [allowedDirty, setAllowedDirty] = useState(false);
  // #P001 (UC-3): "" = kein Agent-Default (Plattform-Default greift) — der Select-Wert ist ein
  // String, `null` am Wire.
  const [defaults, setDefaults] = useState<Record<Kontext, string>>({
    chat: agent.modelPolicy?.defaults?.chat ?? "",
    case: agent.modelPolicy?.defaults?.case ?? "",
  });
  // Review MED-1: Dirty PRO Kontext — nur der geänderte Key geht in den Patch. Ein unveränderter
  // Bestandswert im anderen Feld, den das Universum nicht mehr kennt („nicht mehr wählbar"),
  // würde sonst mitgeschickt, vom Master abgelehnt und blockierte jedes Speichern.
  const [defaultsDirty, setDefaultsDirty] = useState<Record<Kontext, boolean>>({ chat: false, case: false });

  useEffect(() => {
    if (!fixDirty) setFixedModel(agent.modelPolicy?.fixedModel ?? null);
    if (!allowedDirty) setAllowedModels(agent.modelPolicy?.allowedModels ?? []);
    setDefaults((prev) => ({
      chat: defaultsDirty.chat ? prev.chat : (agent.modelPolicy?.defaults?.chat ?? ""),
      case: defaultsDirty.case ? prev.case : (agent.modelPolicy?.defaults?.case ?? ""),
    }));
  }, [agent.modelPolicy, fixDirty, allowedDirty, defaultsDirty]);

  const isPending = patchManaged.isPending || updatePolicy.isPending;
  const dirty = fixDirty || allowedDirty || defaultsDirty.chat || defaultsDirty.case;

  // Optionen aller drei Hebel: das Universum mit Ziel-Anzeige. Fehlt `universe` (älterer
  // Master), bleibt die Liste leer — dann stehen nur „Plattform-Default" und der Bestandswert
  // zur Wahl, statt eine erfundene Auswahl anzubieten.
  const universeTargets = agentModels.data?.universe ?? [];
  const universum = universeTargets.map((t) => ({
    value: t.name,
    label: modelOptionLabel(t),
  }));
  // Ein gespeicherter Default, den das Universum nicht (mehr) kennt, darf nicht still
  // verschwinden — sonst sähe das Feld aus wie „kein Default", obwohl einer gesetzt ist.
  // „(nicht mehr wählbar)" nur, wenn die Auskunft belastbar ist (Universum GELADEN) — beim Laden
  // und bei einem älteren Master ohne `universe` wäre das Urteil erfunden (Review LOW-3).
  const universumBekannt = agentModels.isSuccess && agentModels.data?.universe !== undefined;
  const defaultOptionen = (kontext: Kontext) => mitAltwert(universum, defaults[kontext], universumBekannt);

  // #P001 (Live-Fund 3): Freitext NUR noch als Version-Skew-Rückfall (siehe Kopf-Kommentar).
  const [freierName, setFreierName] = useState("");
  // Ein gespeicherter Eintrag, den das Universum nicht (mehr) kennt — ein gelöschter oder
  // deaktivierter Slot. Er bleibt als eigene Zeile stehen, sonst wäre er unsichtbar und nicht
  // entfernbar (Muster `mitAltwert` bei den Selects).
  // ⚠ Nur mit BELASTBARER Auskunft: solange die Liste lädt oder ein älterer Master sie gar nicht
  // liefert, wäre „nicht mehr wählbar" ein erfundenes Urteil — und JEDER Eintrag träfe es.
  const bestandAusserhalb = universumBekannt
    ? allowedModels.filter((m) => !universeTargets.some((t) => t.name === m))
    : [];
  // Ohne belastbares Universum sind die gespeicherten Einträge die einzige Liste, die wir zeigen
  // können — ohne Urteil über ihre Gültigkeit, aber abwählbar.
  const skewEintraege = universumBekannt || agentModels.isLoading ? [] : allowedModels;
  // Der Freitext erscheint genau dann, wenn es keine Auswahl geben KANN.
  const skewRueckfall = !universumBekannt && !agentModels.isLoading;

  function toggleAllowed(model: string, checked: boolean) {
    setAllowedDirty(true);
    setAllowedModels((prev) =>
      checked ? [...new Set([...prev, model])] : prev.filter((m) => m !== model),
    );
  }

  function freienNamenHinzufuegen() {
    const n = freierName.trim();
    if (n.length === 0) return;
    setAllowedDirty(true);
    setAllowedModels((prev) => [...new Set([...prev, n])]);
    setFreierName("");
  }

  async function save() {
    try {
      // Admin-Fix über die Config-API (config.managed.model). "kein Fix" → "" (der
      // Master wertet leer als "kein Fix"; PATCH-merge kann keinen Key löschen).
      if (fixDirty) {
        await patchManaged.mutateAsync({ agentId: agent.id, payload: { model: fixedModel ?? "" } });
      }
      // Whitelist und Defaults hängen an DERSELBEN Route — ein Patch, nur die geänderten Keys.
      const patch: ModelPolicyPatch = {};
      if (allowedDirty) patch.allowedModels = allowedModels;
      const geaendert = (Object.keys(defaultsDirty) as Kontext[]).filter((k) => defaultsDirty[k]);
      if (geaendert.length > 0) {
        patch.defaults = Object.fromEntries(geaendert.map((k) => [k, defaults[k] === "" ? null : defaults[k]]));
      }
      if (Object.keys(patch).length > 0) {
        await updatePolicy.mutateAsync(patch);
      }
      setFixDirty(false);
      setAllowedDirty(false);
      setDefaultsDirty({ chat: false, case: false });
      toast.success("Modell-Governance gespeichert");
    } catch (err) {
      // Der Master sagt nur „unknown model", nicht WELCHER — die Einträge ausserhalb des
      // Universums sind die Verdächtigen (ein Slot, den es an diesem Agenten nicht mehr oder
      // nicht mehr aktiv gibt).
      const verdacht =
        bestandAusserhalb.length > 0
          ? ` — prüfe die Einträge ausserhalb der Liste: ${bestandAusserhalb.join(", ")}`
          : "";
      toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}${verdacht}`);
    }
  }

  return (
    <SectionCard
      icon={<Cpu className="h-[18px] w-[18px]" />}
      title="Modell-Governance"
      description="Admin-Fix (managed.model) + Owner-Whitelist für /model — Liste dynamisch aus dem SDK"
    >
      <div className="space-y-4">
        {/* Admin-Fix */}
        <div>
          <label className="mb-1 block text-[12px] font-semibold text-text2">
            Admin-Fix (erzwungenes Modell)
          </label>
          <select
            value={fixedModel ?? ""}
            onChange={(e) => { setFixedModel(e.target.value === "" ? null : e.target.value); setFixDirty(true); }}
            className="flex h-10 w-full rounded-md border border-input bg-base px-3 py-2 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-base disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isPending}
            data-testid="model-policy-fixed-model"
          >
            <option value="">
              {agentModels.isLoading ? "Modelle werden geladen…" : "— kein Fix (Owner wählt)"}
            </option>
            {/* #P001 (Live-Fund 3): dasselbe Universum wie die Whitelist und die Defaults —
                inklusive Slots mit Ziel-Label. `mitAltwert` hält einen gesetzten Wert, den die
                Liste nicht (mehr) kennt, als letzte Option fest; ohne ihn sähe die Auswahl aus
                wie „kein Fix", obwohl einer gesetzt ist. */}
            {mitAltwert(universum, fixedModel, universumBekannt).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span className="mt-1 block text-[11px] text-text3">
            Gesetzt ⇒ alle Turns laufen mit diesem Modell, der Owner kann es NICHT per /model wechseln.
          </span>
        </div>

        {/* Whitelist */}
        <div>
          <label className="mb-1.5 block text-[12px] font-semibold text-text2">
            Owner-Whitelist (für /model)
          </label>
          {/* #P001 (Live-Fund 3): die Auswahl IST das Universum dieses Agenten — Anthropic-
              Modelle und jeder AKTIVE Slot, in derselben Reihenfolge und mit demselben Label
              wie die Default-Picker darunter. Ein inaktiver Slot steht gar nicht erst zur Wahl
              (er wäre kein gültiger Modellname). */}
          {agentModels.isLoading ? (
            <span className="block text-[11.5px] text-text3">Modelle werden geladen…</span>
          ) : universumBekannt ? (
            universum.length === 0 ? (
              <span className="block text-[11.5px] text-text3">
                Für diesen Agenten ist kein Modell verfügbar (kein Katalog gemeldet, kein aktiver Slot).
              </span>
            ) : (
              <div className="space-y-2.5">
                {universum.map((o) => (
                  <FlagRow
                    key={o.value}
                    label={o.label}
                    hint=""
                    checked={allowedModels.includes(o.value)}
                    disabled={isPending || fixedModel != null}
                    onChange={(v) => toggleAllowed(o.value, v)}
                    testId={`model-policy-allowed-${o.value}`}
                  />
                ))}
              </div>
            )
          ) : (
            /* Version-Skew bzw. Ladefehler: es gibt keine Auswahl, die wir anbieten könnten.
               Dann bleiben die gespeicherten Einträge (abwählbar, ohne Urteil) und der Freitext
               — sonst liesse sich die Whitelist gar nicht mehr pflegen. */
            <div className="space-y-2.5" data-testid="model-policy-skew">
              {skewEintraege.map((m) => (
                <FlagRow
                  key={m}
                  label={m}
                  hint=""
                  checked
                  disabled={isPending || fixedModel != null}
                  onChange={(v) => toggleAllowed(m, v)}
                  testId={`model-policy-allowed-${m}`}
                />
              ))}
              <span className="block text-[11.5px] text-text3">
                Die Modell-Liste dieses Agenten ist nicht abrufbar — trage den Namen ausnahmsweise
                von Hand ein. Ob es ihn gibt, prüft der Master beim Speichern.
              </span>
            </div>
          )}

          {/* Ein gespeicherter Eintrag, den das Universum nicht mehr kennt (gelöschter oder
              deaktivierter Slot): sichtbar und entfernbar, aber nicht mehr wählbar. */}
          {bestandAusserhalb.length > 0 && (
            <div className="mt-2.5 space-y-2.5" data-testid="model-policy-extra-list">
              {bestandAusserhalb.map((m) => (
                <FlagRow
                  key={m}
                  label={`${m} (nicht mehr wählbar)`}
                  hint=""
                  checked
                  disabled={isPending || fixedModel != null}
                  onChange={(v) => toggleAllowed(m, v)}
                  testId={`model-policy-allowed-${m}`}
                />
              ))}
            </div>
          )}

          {skewRueckfall && (
            <div className="mt-2.5 flex gap-2">
              <input
                className="flex h-9 w-full rounded-md border border-input bg-base px-3 py-1.5 text-[13px] text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={freierName}
                onChange={(e) => setFreierName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); freienNamenHinzufuegen(); } }}
                placeholder="Modell- oder Slot-Name (z.B. deepseek-v4)"
                disabled={isPending || fixedModel != null}
                data-testid="model-policy-allowed-input"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={freienNamenHinzufuegen}
                disabled={isPending || fixedModel != null || freierName.trim().length === 0}
                data-testid="model-policy-allowed-add"
              >
                Hinzufügen
              </Button>
            </div>
          )}
          <span className="mt-1 block text-[11px] text-text3">
            Aufgeführt ist alles, was dieser Agent nutzen kann: die Anthropic-Modelle und jeder
            <b> aktive Slot</b> aus „LLM-Provider". Leere Whitelist ⇒ der Owner darf aus allen wählen.
          </span>
        </div>

        {/* #P001 (UC-3): Kontext-Defaults */}
        <div>
          <label className="mb-1.5 block text-[12px] font-semibold text-text2">
            Standard-Modelle (neue Sessions bzw. Läufe)
          </label>
          <div className="flex flex-col gap-2.5 sm:flex-row">
            {(["chat", "case"] as const).map((kontext) => (
              <div key={kontext} className="min-w-0 flex-1">
                <label className="mb-1 block text-[11.5px] text-text3">
                  {kontext === "chat" ? "Standard-Modell Chat" : "Standard-Modell Prozess-Läufe"}
                </label>
                <select
                  value={defaults[kontext]}
                  onChange={(e) => {
                    setDefaults((prev) => ({ ...prev, [kontext]: e.target.value }));
                    setDefaultsDirty((d) => ({ ...d, [kontext]: true }));
                  }}
                  className={SELECT_CLASS}
                  disabled={isPending || agentModels.isLoading}
                  data-testid={`model-policy-default-${kontext}`}
                >
                  <option value="">
                    {agentModels.isLoading ? "Modelle werden geladen…" : "Plattform-Default"}
                  </option>
                  {defaultOptionen(kontext).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          {agentModels.isError && (
            <p className="mt-1 text-[11px] text-danger" data-testid="model-policy-default-fehler">
              Modell-Liste nicht abrufbar.{" "}
              <button type="button" className="underline" onClick={() => void agentModels.refetch()}>
                Erneut laden
              </button>
            </p>
          )}
          <span className="mt-1 block text-[11px] text-text3">
            Gilt für NEUE Sessions bzw. Läufe; ein Admin-Fix übersteuert; der Owner weicht per
            /model, Instanz oder Start ab.
          </span>
        </div>

        {/* Hilfetext + Speichern */}
        <div className="flex items-end justify-between gap-3">
          <span className="text-[11px] text-text3">
            Admin-Fix ⇒ der Owner kann das Modell nicht per /model wechseln (Whitelist dann irrelevant).
            Leere Whitelist ⇒ der Owner darf aus dem ganzen Katalog wählen.
          </span>
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || isPending}
            data-testid="model-policy-save"
          >
            <Save className="h-4 w-4" />
            Speichern
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}

// 1:1 wie in DiscoverySection (FlagRow) — Checkbox + Label + Hint.
function FlagRow({
  label, hint, checked, disabled, onChange, testId,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  testId: string;
}) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
      />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-text">{label}</span>
        {hint && <span className="block text-[11.5px] text-text3">{hint}</span>}
      </span>
    </label>
  );
}
